import { type DBSchema, type IDBPDatabase, openDB } from 'idb'

import type { UpdateConversationPayload } from '@/hooks/use-conversation-actions'

export type AttendanceActionStatus = 'queued' | 'sending' | 'failed'

export type AttendanceActionKind =
  | 'update'
  | 'assume'
  | 'assign'
  | 'transfer'
  | 'finalize'
  | 'archive'

export type AttendanceActionPayload =
  | UpdateConversationPayload
  | { userId: string | null }
  | { departmentId: string; assignedUserId?: string | null }
  | { reasonId?: string; reasonCode?: string }
  | null

export interface AttendanceActionItem {
  id: string
  orgId: string
  conversationId: string
  kind: AttendanceActionKind
  payload: AttendanceActionPayload
  createdAt: string
  status: AttendanceActionStatus
  failureReason?: string | null
}

interface ActionOutboxDB extends DBSchema {
  actions: {
    key: string
    value: AttendanceActionItem
    indexes: { 'by-conversation': string; 'by-status': AttendanceActionStatus }
  }
}

let dbPromise: Promise<IDBPDatabase<ActionOutboxDB>> | null = null

function getDB() {
  if (!dbPromise) {
    dbPromise = openDB<ActionOutboxDB>('agrosdr-inbox-action-outbox', 1, {
      upgrade(db) {
        const store = db.createObjectStore('actions', { keyPath: 'id' })
        store.createIndex('by-conversation', 'conversationId')
        store.createIndex('by-status', 'status')
      },
    })
  }
  return dbPromise
}

export async function putAttendanceAction(
  item: AttendanceActionItem
): Promise<void> {
  const db = await getDB()
  await db.put('actions', item)
}

export async function deleteAttendanceAction(id: string): Promise<void> {
  const db = await getDB()
  await db.delete('actions', id)
}

export async function getAllAttendanceActions(): Promise<
  AttendanceActionItem[]
> {
  const db = await getDB()
  return db.getAll('actions')
}
